// Pure, browser-local helpers for the widget/embed generator tools:
// invoice totals, HTML escaping, Code128 barcode encoding, and WhatsApp links.
// Dependency-free; safe to import on server or client.

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// --- Invoice totals --------------------------------------------------------

export type InvoiceLineItem = {
  description: string;
  quantity: string;
  price: string;
};

export type InvoiceTotals = {
  lines: { description: string; quantity: number; price: number; amount: number }[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
};

function toNumber(value: string): number {
  const n = Number.parseFloat((value || '').replace(/,/g, '.'));
  return Number.isFinite(n) ? n : 0;
}

export function computeInvoiceTotals(items: InvoiceLineItem[], taxPercent: string): InvoiceTotals {
  const lines = items.map((item) => {
    const quantity = toNumber(item.quantity);
    const price = toNumber(item.price);
    return {
      description: item.description,
      quantity,
      price,
      amount: quantity * price,
    };
  });
  const subtotal = lines.reduce((sum, line) => sum + line.amount, 0);
  const taxRate = toNumber(taxPercent);
  const taxAmount = subtotal * (taxRate / 100);
  return {
    lines,
    subtotal,
    taxRate,
    taxAmount,
    total: subtotal + taxAmount,
  };
}

export function formatMoney(amount: number, currency: string): string {
  const symbol = currency || '';
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return symbol ? `${symbol}${formatted}` : formatted;
}

// --- Code128 barcode encoding ----------------------------------------------
// Implements Code128 (auto A/B/C not required) using Code Set B for the full
// printable ASCII range, with the standard checksum and Bar/Space patterns.

// 107 patterns (values 0-106). Each is a string of six digits giving the
// widths of bar,space,bar,space,bar,space (modules). Index 106 is the stop
// pattern which has an extra terminating bar (7 elements).
const CODE128_PATTERNS = [
  '212222',
  '222122',
  '222221',
  '121223',
  '121322',
  '131222',
  '122213',
  '122312',
  '132212',
  '221213',
  '221312',
  '231212',
  '112232',
  '122132',
  '122231',
  '113222',
  '123122',
  '123221',
  '223211',
  '221132',
  '221231',
  '213212',
  '223112',
  '312131',
  '311222',
  '321122',
  '321221',
  '312212',
  '322112',
  '322211',
  '212123',
  '212321',
  '232121',
  '111323',
  '131123',
  '131321',
  '112313',
  '132113',
  '132311',
  '211313',
  '231113',
  '231311',
  '112133',
  '112331',
  '132131',
  '113123',
  '113321',
  '133121',
  '313121',
  '211331',
  '231131',
  '213113',
  '213311',
  '213131',
  '311123',
  '311321',
  '331121',
  '312113',
  '312311',
  '332111',
  '314111',
  '221411',
  '431111',
  '111224',
  '111422',
  '121124',
  '121421',
  '141122',
  '141221',
  '112214',
  '112412',
  '122114',
  '122411',
  '142112',
  '142211',
  '241211',
  '221114',
  '413111',
  '241112',
  '134111',
  '111242',
  '121142',
  '121241',
  '114212',
  '124112',
  '124211',
  '411212',
  '421112',
  '421211',
  '212141',
  '214121',
  '412121',
  '111143',
  '111341',
  '131141',
  '114113',
  '114311',
  '411113',
  '411311',
  '113141',
  '114131',
  '311141',
  '411131',
  '211412',
  '211214',
  '211232',
  '2331112',
];

const CODE128_START_B = 104;
const CODE128_STOP = 106;

export type Code128Result =
  | { ok: true; svg: string; width: number; height: number }
  | { ok: false; error: string };

// Map a Code Set B character (ASCII 32-126) to its Code128 value (0-94).
function charToValueB(charCode: number): number | null {
  if (charCode >= 32 && charCode <= 126) return charCode - 32;
  return null;
}

export type Code128Options = {
  moduleWidth?: number;
  height?: number;
  showText?: boolean;
};

export function encodeCode128(text: string, options: Code128Options = {}): Code128Result {
  if (!text) {
    return { ok: false, error: 'Enter some text or numbers to generate a barcode.' };
  }

  const values: number[] = [CODE128_START_B];
  for (const char of text) {
    const value = charToValueB(char.charCodeAt(0));
    if (value === null) {
      return {
        ok: false,
        error: `The character "${char}" is not supported. Use printable ASCII (letters, digits, common symbols).`,
      };
    }
    values.push(value);
  }

  // Checksum: start value + sum(value_i * position_i), mod 103.
  let checksum = CODE128_START_B;
  for (let i = 1; i < values.length; i += 1) {
    checksum += values[i] * i;
  }
  checksum %= 103;
  values.push(checksum);
  values.push(CODE128_STOP);

  const moduleWidth = options.moduleWidth ?? 2;
  const height = options.height ?? 80;
  const showText = options.showText ?? true;
  const quietZone = 10 * moduleWidth;
  const textHeight = showText ? 22 : 0;

  // Build the bar/space module string, then convert runs into SVG rects.
  let modules = '';
  for (const value of values) {
    const pattern = CODE128_PATTERNS[value];
    let isBar = true;
    for (const widthChar of pattern) {
      const width = Number.parseInt(widthChar, 10);
      modules += (isBar ? '1' : '0').repeat(width);
      isBar = !isBar;
    }
  }

  const totalModules = modules.length;
  const barcodeWidth = totalModules * moduleWidth;
  const svgWidth = barcodeWidth + quietZone * 2;
  const svgHeight = height + textHeight;

  const rects: string[] = [];
  let x = quietZone;
  let runStart = 0;
  while (runStart < totalModules) {
    let runEnd = runStart;
    while (runEnd < totalModules && modules[runEnd] === modules[runStart]) {
      runEnd += 1;
    }
    const runLength = runEnd - runStart;
    if (modules[runStart] === '1') {
      rects.push(
        `<rect x="${x}" y="0" width="${runLength * moduleWidth}" height="${height}" fill="#111827" />`,
      );
    }
    x += runLength * moduleWidth;
    runStart = runEnd;
  }

  const label = showText
    ? `<text x="${svgWidth / 2}" y="${height + 17}" text-anchor="middle" font-family="monospace" font-size="16" fill="#111827">${escapeHtml(text)}</text>`
    : '';

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}" role="img" aria-label="Barcode: ${escapeHtml(text)}"><rect width="${svgWidth}" height="${svgHeight}" fill="#ffffff" />${rects.join('')}${label}</svg>`;

  return { ok: true, svg, width: svgWidth, height: svgHeight };
}

// --- WhatsApp link ---------------------------------------------------------

export type WhatsappLinkResult = {
  digits: string;
  link: string;
  apiLink: string;
  buttonHtml: string;
  error: string | null;
};

export function buildWhatsappLink(
  phone: string,
  message: string,
  buttonLabel: string,
): WhatsappLinkResult {
  const digits = (phone || '').replace(/[^\d]/g, '');
  const encodedMessage = message ? encodeURIComponent(message) : '';
  const query = encodedMessage ? `?text=${encodedMessage}` : '';
  const link = `https://wa.me/${digits}${query}`;
  const apiLink = digits
    ? `https://api.whatsapp.com/send?phone=${digits}${encodedMessage ? `&text=${encodedMessage}` : ''}`
    : `https://api.whatsapp.com/send${encodedMessage ? `?text=${encodedMessage}` : ''}`;

  const label = buttonLabel.trim() || 'Chat on WhatsApp';
  const buttonHtml = `<a href="${escapeHtml(link)}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:8px;background:#25d366;color:#ffffff;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:600;text-decoration:none;padding:12px 20px;border-radius:9999px;">
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#ffffff" xmlns="http://www.w3.org/2000/svg"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm5.8 14.03c-.24.68-1.2 1.26-1.98 1.42-.53.11-1.22.2-3.56-.76-2.99-1.24-4.91-4.28-5.06-4.48-.15-.2-1.2-1.6-1.2-3.05s.76-2.16 1.03-2.46c.27-.3.59-.37.78-.37.2 0 .39 0 .56.01.18.01.42-.07.66.5.24.58.83 2.03.9 2.18.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.18-.32.4-.45.53-.15.15-.31.31-.13.61.18.3.8 1.32 1.72 2.14 1.18 1.05 2.18 1.38 2.48 1.53.3.15.48.13.66-.08.18-.2.76-.89.96-1.19.2-.3.4-.25.66-.15.27.1 1.72.81 2.01.96.3.15.49.22.56.35.07.13.07.73-.17 1.41z"/></svg>
  ${escapeHtml(label)}
</a>`;

  const error = digits ? null : 'Enter a phone number (with country code, digits only).';

  return { digits, link, apiLink, buttonHtml, error };
}

// --- Email signature -------------------------------------------------------

export type EmailSignatureValues = {
  name: string;
  title: string;
  company: string;
  phone: string;
  email: string;
  website: string;
  logo: string;
  accent: string;
};

function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function displayUrl(url: string): string {
  return url
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/$/, '');
}

export function buildEmailSignatureHtml(values: EmailSignatureValues): string {
  const accent = values.accent.trim() || '#2563eb';
  const name = escapeHtml(values.name.trim());
  const title = escapeHtml(values.title.trim());
  const company = escapeHtml(values.company.trim());
  const phone = values.phone.trim();
  const email = values.email.trim();
  const websiteRaw = values.website.trim();
  const websiteHref = normalizeUrl(websiteRaw);
  const websiteText = escapeHtml(displayUrl(websiteRaw));
  const logo = values.logo.trim();

  const titleCompany = [title, company].filter(Boolean).join(', ');

  const contactRows: string[] = [];
  if (phone) {
    contactRows.push(
      `<span style="color:#4b5563;">P: <a href="tel:${escapeHtml(phone.replace(/[^\d+]/g, ''))}" style="color:#4b5563;text-decoration:none;">${escapeHtml(phone)}</a></span>`,
    );
  }
  if (email) {
    contactRows.push(
      `<span style="color:#4b5563;">E: <a href="mailto:${escapeHtml(email)}" style="color:${escapeHtml(accent)};text-decoration:none;">${escapeHtml(email)}</a></span>`,
    );
  }
  if (websiteHref) {
    contactRows.push(
      `<span style="color:#4b5563;">W: <a href="${escapeHtml(websiteHref)}" style="color:${escapeHtml(accent)};text-decoration:none;">${websiteText}</a></span>`,
    );
  }

  const contactHtml = contactRows.join('<span style="color:#d1d5db;"> &nbsp;|&nbsp; </span>');

  const logoCell = logo
    ? `<td style="padding-right:16px;vertical-align:top;"><img src="${escapeHtml(logo)}" alt="${company || name}" width="64" style="display:block;border:0;border-radius:8px;" /></td>`
    : '';

  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:14px;line-height:1.5;color:#111827;">
  <tr>
    ${logoCell}
    <td style="vertical-align:top;border-left:3px solid ${escapeHtml(accent)};padding-left:16px;">
      <div style="font-size:16px;font-weight:700;color:#111827;">${name}</div>
      ${titleCompany ? `<div style="font-size:13px;color:#6b7280;margin-top:2px;">${titleCompany}</div>` : ''}
      ${contactHtml ? `<div style="font-size:13px;margin-top:8px;">${contactHtml}</div>` : ''}
    </td>
  </tr>
</table>`;
}
